import React, { useState, useRef, useEffect } from 'react';
import { useAskLotus } from '../../contexts/askLotus/AskLotusContext';
import MessageBubble from './MessageBubble';
import { sendMessage as sendMessageService } from '../../lib/services/messageService';

const ChatWindow: React.FC = () => {
  const {
    messages,
    isLoading,
    error,
    guestEmail,
    setGuestEmail,
    guestName,
    setGuestName,
    needsEmail,
    sendMessage,
    startConversation,
    startNewConversation,
    conversation
  } = useAskLotus();

  const [inputValue, setInputValue] = useState('');
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim() || isSending) return;

    setIsSending(true);
    const success = await sendMessage(inputValue);
    if (success) {
      setInputValue('');
    }
    setIsSending(false);
    inputRef.current?.focus();
  };

  // Email form for guests
  if (needsEmail) {
    return (
      <div className="flex flex-col h-full">
        {/* Welcome message */}
        <div className="flex-1 p-4 overflow-y-auto">
          <div className="bg-[#E8F5F2] rounded-2xl rounded-bl-md px-4 py-3 max-w-[85%]">
            <p className="text-sm text-[#344854]">
              Hi there! 👋 Welcome to Lotus Healing Spa. How can we help you today?
            </p>
            <p className="text-sm text-[#344854] mt-2">
              Please enter your email so we can get back to you.
            </p>
          </div>
        </div>

        {/* Email input form */}
        <div className="border-t border-gray-200 p-4 bg-white">
          {error && (
            <div className="text-red-500 text-xs mb-2">{error}</div>
          )}
          <div className="space-y-3">
            <input
              type="email"
              value={guestEmail}
              onChange={(e) => setGuestEmail(e.target.value)}
              placeholder="Your email *"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-[#3A5E63] focus:border-transparent"
              required
            />
            <input
              type="text"
              value={guestName}
              onChange={(e) => setGuestName(e.target.value)}
              placeholder="Your name (optional)"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-[#3A5E63] focus:border-transparent"
            />
            <button
              onClick={async () => {
                setIsSending(true);
                const success = await startConversation();
                if (success) {
                  // Small delay to ensure realtime subscription is set up
                  await new Promise(resolve => setTimeout(resolve, 100));
                  // Send welcome message from staff
                  const session = sessionStorage.getItem('ask_lotus_guest_session');
                  if (session) {
                    const { conversationId } = JSON.parse(session);
                    await sendMessageService(
                      conversationId,
                      'Thanks for reaching out! How can we help you today?',
                      'staff',
                      null
                    );
                  }
                }
                setIsSending(false);
              }}
              disabled={!guestEmail || isSending}
              className="w-full bg-[#3A5E63] text-white py-2 rounded-lg text-sm font-medium hover:bg-[#2d4a4f] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSending ? 'Starting...' : 'Start Chat'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Messages area */}
      <div className="flex-1 p-4 overflow-y-auto bg-gray-50">
        {isLoading && messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3A5E63]"></div>
          </div>
        ) : messages.length === 0 ? (
          <div className="bg-[#E8F5F2] rounded-2xl rounded-bl-md px-4 py-3 max-w-[85%]">
            <p className="text-sm text-[#344854]">
              Hi there! 👋 Welcome to Lotus Healing Spa. How can we help you today?
            </p>
          </div>
        ) : (
          <>
            {messages.map((message) => (
              <MessageBubble key={message.id} message={message} />
            ))}
          </>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Error message */}
      {error && (
        <div className="px-4 py-2 bg-red-50 text-red-600 text-xs">
          {error}
        </div>
      )}

      {/* Conversation closed notice */}
      {conversation?.status === 'closed' && (
        <div className="px-4 py-4 bg-gray-100 border-t border-gray-200">
          <p className="text-gray-600 text-sm text-center mb-3">
            This conversation has been closed.
          </p>
          <button
            onClick={startNewConversation}
            className="w-full bg-[#3A5E63] text-white py-2 rounded-lg text-sm font-medium hover:bg-[#2d4a4f] transition-colors"
          >
            Start New Conversation
          </button>
        </div>
      )}

      {/* Input area */}
      {conversation?.status !== 'closed' && (
        <form onSubmit={handleSubmit} className="border-t border-gray-200 p-3 bg-white">
          <div className="flex items-center gap-2">
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Type a message..."
              className="flex-1 px-3 py-2 border border-gray-300 rounded-full text-base focus:outline-none focus:ring-2 focus:ring-[#3A5E63] focus:border-transparent"
              disabled={isSending}
            />
            <button
              type="submit"
              disabled={!inputValue.trim() || isSending}
              className="bg-[#3A5E63] text-white p-2 rounded-full hover:bg-[#2d4a4f] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="w-5 h-5"
              >
                <path d="M3.478 2.405a.75.75 0 00-.926.94l2.432 7.905H13.5a.75.75 0 010 1.5H4.984l-2.432 7.905a.75.75 0 00.926.94 60.519 60.519 0 0018.445-8.986.75.75 0 000-1.218A60.517 60.517 0 003.478 2.405z" />
              </svg>
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default ChatWindow;
